// Sliding-window classifier that keeps transient gateway 5xx (502/503/504 and
// Cloudflare 520-527) from flooding Rollbar during a deploy/outage reload
// storm — WITHOUT hiding real incidents. Per signature (status + PAGE url
// without its query string) within TRANSIENT_WINDOW_MS: report the first
// occurrence as 'warning' (an isolated blip), drop the noisy middle (dedupe
// the reload storm), and re-report ONCE as 'error' when the count hits
// TRANSIENT_BURST_THRESHOLD so a sustained outage stays visible on the
// dashboard.
const TRANSIENT_WINDOW_MS = 10000;
const TRANSIENT_BURST_THRESHOLD = 5;
const transientHits = [];

// Serializes ONLY the occurrence's own error data for pattern matching: the
// exception + stack (`body.trace`, or `body.trace_chain` for chained causes) or
// the logged message (`body.message`). Never the whole payload: rollbar attaches
// `body.telemetry` (up to 100 recent events, including every earlier logged
// error and each XHR/fetch `url` + `status_code`) before the predicates run, so
// one earlier Clerk load failure or 504 in the session would otherwise mark
// every later real error as noise (dropped) or transient (downgraded/deduped).
// Returns null when the data can't be serialized (circular etc.); each caller
// then fails safe towards reporting the occurrence unchanged.
function ownErrorText(payload) {
  const body = payload?.body || {};
  try {
    return JSON.stringify([body.trace, body.trace_chain, body.message]);
  } catch (e) {
    return null;
  }
}

// Returns a { sig } for a transient gateway 5xx — 502/503/504, or Cloudflare's
// 520-527 edge errors (e.g. 522, origin connection timed out) — or null for
// anything else (non-Axios errors, 4xx, app-level 500s and every other 5xx →
// reported normally, never touched). The status is read from Axios' message
// ("status code 522") or a serialized status field ("status_code": 522); the
// trailing \b keeps longer numbers (e.g. 5220) from matching.
function classifyTransient(payload) {
  const text = ownErrorText(payload);
  if (text === null) return null; // unserializable → treat as non-transient
  const status = text.match(/(?:status code |"status(?:_code)?":\s*)(5(?:0[234]|2[0-7]))\b/);
  if (!status) return null;
  // `request.url` is the PAGE the user was on (rollbar fills it from
  // window.location.href), not the failing API url. The query string is
  // stripped so reloads of the same page with different params share a
  // signature.
  const pageUrl = String(payload.request?.url || '').split('?')[0];
  return { sig: `${status[1]}|${pageUrl}` };
}

function transientCountInWindow(sig, now) {
  while (transientHits.length && now - transientHits[0].t > TRANSIENT_WINDOW_MS) {
    transientHits.shift();
  }
  return transientHits.reduce((n, h) => n + (h.sig === sig ? 1 : 0), 0);
}

// Third-party / bot noise that is never actionable in our own code and only
// buries real issues. Dropped outright (see checkIgnore):
//  - Clerk auth SDK loaded from clerk.crafter.ai (Clerk's CDN): script/chunk
//    load failures and session-touch network errors are the user's network,
//    an ad/privacy blocker, or a Clerk-side incident — not our bug. The user
//    still gets the in-app "having trouble loading" fallback on the auth page.
//  - Browser extensions & link scanners: MetaMask, MS Outlook SafeLinks
//    ("Object Not Found Matching Id"), and CORS-masked "Script error." items
//    (no stack, no actionable detail).
//  - Errors THROWN by browser-extension code (matched by isExtensionError, not
//    by a pattern): third-party code injected into our page that we cannot fix,
//    e.g. the SideeX recorder's `monkey-patch.js` reading `responseText` on our
//    blob XHRs, or its "response is not defined". Only the innermost (throwing)
//    frame counts — see isExtensionError for why outer frames don't.
//  - "ResizeObserver loop completed with undelivered notifications" / "loop
//    limit exceeded": a benign browser notice (the observer skipped a frame of
//    layout updates and delivers them on the next one) with no user impact.
const NOISE_PATTERNS = [
  /Failed to load Clerk (?:UI|JS)/i,
  /failed_to_load_clerk/i,
  /ClerkJS: Network error/i,
  /clerk\.crafter\.ai\/npm\/@clerk/i,
  /Object Not Found Matching Id/i,
  /Failed to connect to MetaMask/i,
  /Script error\.?"/,
  /ResizeObserver loop completed with undelivered notifications/i,
  /ResizeObserver loop limit exceeded/i,
];

function isKnownNoise(payload) {
  const text = ownErrorText(payload);
  // unserializable → let it through rather than risk hiding a real error
  return text !== null && NOISE_PATTERNS.some((re) => re.test(text));
}

// Script origins of browser extensions (Chromium, Firefox, Safari legacy and
// Safari Web Extensions).
const EXTENSION_URL = /^(?:chrome|moz|safari|safari-web)-extension:\/\//i;

// True when the THROWING frame is browser-extension code. Rollbar orders frames
// most-recent-LAST (browser/transforms.js reverses the parsed stack), so that is
// the LAST frame of `body.trace.frames`, or of `body.trace_chain[0].frames` (the
// top-level error) for chained causes. Outer frames deliberately don't count:
// extensions like SideeX monkey-patch XMLHttpRequest, so OUR OWN errors raised
// inside a request callback can carry outer extension frames — matching any
// frame would hide real bugs. Anything missing/malformed → false (reported).
function isExtensionError(payload) {
  const body = payload?.body || {};
  const frames = (body.trace || body.trace_chain?.[0])?.frames;
  if (!Array.isArray(frames) || frames.length === 0) return false;
  const filename = frames[frames.length - 1]?.filename;
  return typeof filename === 'string' && EXTENSION_URL.test(filename);
}

export default {
  accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  scrubFields: ['password', 'authorization', 'Authorization', 'token', 'accessToken'],
  // Return true to DROP an occurrence. Dropped: known third-party / bot noise
  // (NOISE_PATTERNS, isExtensionError), and every repeat of a transient-5xx
  // signature within the window except the TRANSIENT_BURST_THRESHOLD-th (i.e.
  // the storm's middle AND everything past the burst report). Kept: the first
  // transient occurrence (as 'warning'), the burst one (as 'error'), and every
  // other error as-is.
  //
  // The level is set directly on the payload here, NOT via a `transform` hook:
  // in rollbar 3.x the notifier applies all transforms (including the user
  // `transform`) BEFORE the queue runs its predicates (including `checkIgnore`),
  // so a level decided here and handed to `transform` would land on the NEXT
  // occurrence. The payload checkIgnore receives is the very object that gets
  // transmitted, so mutating its `level` changes what is sent for the current
  // occurrence.
  checkIgnore(isUncaught, args, payload) {
    try {
      if (isKnownNoise(payload) || isExtensionError(payload)) {
        return true; // drop third-party / bot noise (Clerk CDN, extensions, scanners, ResizeObserver)
      }
      const info = classifyTransient(payload);
      if (!info) {
        return false; // not a transient 5xx → report unchanged
      }
      const now = Date.now();
      const count = transientCountInWindow(info.sig, now) + 1;
      transientHits.push({ sig: info.sig, t: now });
      if (count === 1) {
        payload.level = 'warning'; // isolated blip
        return false;
      }
      if (count === TRANSIENT_BURST_THRESHOLD) {
        payload.level = 'error'; // burst confirmed → keep a real outage visible
        return false;
      }
      return true; // dedupe the reload storm
    } catch (e) {
      return false; // never let the filter suppress by throwing
    }
  },
  payload: {
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    client: {
      javascript: {
        code_version: '1.0.0',
        source_map_enabled: true,
      },
    },
  },
};
